<template>
<div class="policy">
<p>
  <strong>STATMUSE PRIVACY POLICY</strong>
</p>
<p>
  <strong>(Last Updated May 29, 2018)</strong>
</p>
<p>StatMuse, Inc. (www.statmuse.com), as well as any present or future affiliated or related company (“StatMuse” or “we”), recognizes
  the importance of your privacy. This privacy policy tells you what personal information we collect and how we use it. StatMuse
  may change this privacy policy from time to time, in its sole discretion, as discussed in section 7 below. By using or
  visiting any StatMuse website, application, product, software, tool, data feed, and/or service (collectively the "Service"),
  you understand and agree to the terms of this policy. Please read this policy carefully. </p>
<p>
  <strong>1. What Information Do We Collect?</strong>
</p>
<p>a.
  <em>Personally-Identifiable Information</em>: We may collect personally identifiable information when you specifically and
  knowingly provide it to us, for example when you contact us and provide personal information such as your e-mail address,
  name, phone number, or other information. Where applicable, personally identifiable information includes “personal data”
  as defined in applicable law. </p>
<p>This Privacy Policy does not apply to the privacy practices of third parties that we do not own or control, including but
  not limited to any third party voice assistants, websites, services, applications, online resources to which the Service
  may link to, be accessed from, or otherwise reference (collectively “Third Party Services” or “TPS”). We encourage you
  to carefully review the privacy policies of any TPS you access. </p>
<p>You may have the option to register or access the Service using a TPS. By authorizing us to access your TPS account, you
  authorize us to obtain certain information from your TPS account, which may include your name, email address, associated
  IP addresses or other available information. We only obtain information from your TPS account that you specifically authorize
  and grant us permission to obtain.</p>
<p>StatMuse does not consider personally identifiable information to include information that has been anonymized so that it
  does not allow a third party to identify a specific individual. We collect and use your personally-identifiable information
  to: provide the Service; operate and improve our Service; provide customer service; perform research and analysis aimed
  at improving our products, Service and technology; and display content that is customized to your interests and preferences.</p>
<p>You may always choose not to provide personally identifiable information, but if you so choose, certain parts of the Service
  may not be available to you. If you have registered an account with us, you will have agreed to provide your personally
  identifiable information in order to access the Service. This consent provides us with the legal basis we require under
  applicable law to process your data. You maintain the right to withdraw such consent at any time. If you do not agree to
  our use of your personal data in line with this Policy, please do not use our Service. </p>
<p>The Service may be accessed through mobile phones, websites, and audio TSP (such as personal assistant or Artificial Intelligence
  apps) which have their own terms of use and privacy policies, particularly regarding the handling of audio content submitted
  by a user. We encourage you to review such terms and policies as they may apply to your use of the Service. We have no
  responsibility for the terms and policies of any hardware, software, application, or TSP. </p>
<p> b.
  <em>Sensitive Personal Data</em>: Subject to the following paragraph, we ask that you not send us, and you not disclose, any
  sensitive personal data (e.g., social security numbers, information related to racial or ethnic origin, political opinions,
  religion or other beliefs, health, biometrics or genetic characteristics, criminal background or trade union membership)
  on or through the Service or otherwise to us. </p>
<p>If you send or disclose any sensitive personal data to us when you submit content to the Service, you consent to our processing
  and use of such sensitive personal data in accordance with this Policy. If you do not consent to our processing and use
  of such sensitive personal data, you must not submit such content to us.</p>
<p>c.
  <em>Non-Personally-Identifiable Information</em>: We may collect and aggregate non-personally identifiable information which
  is information which does not permit you to be identified or identifiable either by itself or in combination with other
  information available to a third party. This information may include information such as a website that referred you to
  us, your IP address, browser type and language, hardware types, geographic location, access times and durations, queries
  submitted to the Service (including text translation of any audio queries). We also may collect navigational information,
  including information about the Service content you access or are served in response to your written or audio request,
  or pages you view, the links you click, and other actions taken in connection with the Service. We use this information
  to analyze usage patterns as part of making improvements to the Service. </p>
<p>d.
  <em>Cookies, Pixels and Local Storage</em>: We may collect information using “cookies”, which are small data files stored on
  the hard drive of your computer or mobile device by a website. We may use both session cookies (which expire once you close
  your web browser) and persistent cookies (which stay on your computer or mobile device until you delete them) to provide
  you with a more personal and interactive experience when using the Service. </p>
<p>We use two broad categories of cookies: (1) first party cookies, served directly by us to your computer or mobile device,
  which are used only by us to recognize your computer or mobile device when it revisits any site or application that is
  party of the Service; and (2) third party cookies, which are served by service providers on sites or applications and can
  be used by such service providers to recognize your computer or mobile device when it visits other websites.</p>
<h5>Cookies we use</h5>

<p>The Service uses the following types of cookies for the purposes set out below:</p>
<table>
  <tr>
    <td>
      <p>
        <strong>Type of cookie</strong>
      </p>
    </td>
    <td>
      <p>
        <strong>Purpose </strong>
      </p>
    </td>
  </tr>
  <tr>
    <td>
      <p>
        <em>Essential Cookies </em>
      </p>
    </td>
    <td>
      <p>These cookies are essential to provide you with services available through the Service and to enable you to use some
        of its features. For example, they allow you to log in to secure areas of the sites or applications and help the
        content of the pages you request load quickly. Without these cookies, the services that you have asked for cannot
        be provided, and we only use these cookies to provide you with those services.</p>
    </td>
  </tr>
  <tr>
    <td>
      <p>
        <em>Functionality Cookies</em>
      </p>
    </td>
    <td>
      <p>These cookies allow the Service to remember choices you make when you use a site or application, such as remembering
        your language preferences, remembering your login details and remembering the changes you make to other parts of
        your account, or preferences. The purpose of these cookies is to provide you with a more personal experience and
        to avoid you having to re-enter your preferences every time you use the Service. </p>
    </td>
  </tr>
  <tr>
    <td>
      <p>
        <em>Analytics and Performance Cookies</em>
      </p>
    </td>
    <td>
      <p>These cookies are used to collect information about traffic to the Service and how users use the Service. The information
        gathered does not identify any individual visitor. The information is aggregated and anonymous. It includes the number
        of visitors to, the websites that referred them, the pages visited, what time of day they visited, whether they have
        visited before, and other similar information. We use this information to help operate the Service more efficiently,
        to gather broad demographic information and to monitor the level of activity on our Service. </p>
      <p>We use Google Analytics for this purpose. Google Analytics uses its own cookies. It is only used to improve how the
        Service work. You can find out more information about Google Analytics cookies here:
        <a href="https://developers.google.com/analytics/resources/concepts/gaConceptsCookies">https://developers.google.com/analytics/resources/concepts/gaConceptsCookies</a>
      </p>
      <p>You can find out more about how Google protects your data here:
        <a href="http://www.google.com/analytics/learn/privacy.html">www.google.com/analytics/learn/privacy.html</a>.</p>
      <p>You can prevent the use of Google Analytics relating to your use of the Service by downloading and installing the browser
        plugin available via this link:
        <a href="http://tools.google.com/dlpage/gaoptout?hl=en-GB">http://tools.google.com/dlpage/gaoptout?hl=en-GB</a>
      </p>
    </td>
  </tr>
  <tr>
    <td>
      <p>
        <em>Social Media Cookies</em>
      </p>
    </td>
    <td>
      <p>These cookies are used when you share information using a social media sharing button or “like” button on any site
        or application for the Service or you link your account or engage with our content on or through a social networking
        website such as Facebook, Twitter or Google+. The social network will record that you have done this.</p>
    </td>
  </tr>
</table>
<h5>Disabling cookies</h5>
<p>You can typically remove or reject cookies via your browser settings. In order to do this, follow the instructions provided
  by your browser (usually located within the “settings,” “help” “tools” or “edit” facility). Many browsers are set to accept
  cookies until you change your settings. If you do not accept our cookies, you may experience some inconvenience in your
  use of the Service. For example, we may not be able to recognize your computer or mobile device and you may need to log
  in every time you use the Service.</p>
<h5>Pixel Tags </h5>

<p>We may also use pixel tags (which are also known as web beacons and clear GIFs) on the Service to track the actions of users
  on our sites and applications. Unlike cookies, which are stored on the hard drive of your computer or mobile device by
  a website, pixel tags are embedded invisibly on webpages. Pixel tags measure the success of our marketing campaigns and
  compile statistics about usage of the Service, so that we can manage our content more effectively. The information we collect
  using pixel tags is not linked to our users’ personal data.</p>
<h5>Flash Technology</h5>
<p>We may also use Flash cookies (which are also known as Flash Local Shared Object (“LSOs”)) on our Service to collect and
  store information about your use of the Service. Unlike other cookies, Flash cookies cannot be removed or rejected via
  your browser settings. If you do not want Flash cookies stored on your computer or mobile device, you can adjust the settings
  of your Flash player to block Flash LSO storage using the tools contained in the Website Storage Settings Panel at
  <a href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html">https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager07.html</a>. You can also control
  Flash LSOs by going to the Global Storage Settings Panel at
  <a href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html">https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager03.html</a> and following the instructions.
  Please note that setting the Flash Player to restrict or limit acceptance of Flash LSOs may reduce or impede the functionality
  of some Flash applications, including, potentially, Flash applications used in connection with the Service. </p>
<h5>Do Not Track Signals</h5>
<p>Some Internet browsers may be configured to send "Do Not Track" signals to the online services that you visit. We currently
  [do] [do not] respond to do not track signals. To find out more about "Do Not Track," please visit
  <a href="http://www.allaboutdnt.com">http://www.allaboutdnt.com</a>.</p>
<p>
  <strong>2. What Do We Do With The Information That We Collect?</strong>
</p>
<p>Except as disclosed in this privacy policy, StatMuse does not share your personal information with any outside parties.</p>
<p>a. StatMuse will use the personally identifiable information directly provided by you solely for the purpose for which you
  have provided it, whether it be which may include:</p>
<ul>
  <li>to operate, maintain, and improve the Service;</li>
  <li>to manage your account, including to communicate with you regarding your account;</li>
  <li>to operate and administer any promotions you participate in on any site or application;</li>
  <li>to respond to your comments and questions and to provide customer service;</li>
  <li>to send information including technical notices, updates, security alerts, and support and administrative messages;</li>
  <li>with your consent, to send you marketing e-mails about upcoming promotions, and other news, including information about
    products and services offered by us and our affiliates. You may opt-out of receiving such information at any time: such
    marketing emails tell you how to “opt-out.” Please note, even if you opt out of receiving marketing emails, we may still
    send you non-marketing emails. Non-marketing emails include emails about your account with us (if you have one) and our
    business dealings with you;
    <strong> </strong>
  </li>
  <li>to process payments you make via the Service;
    <strong> </strong>and</li>
  <li>as we believe necessary or appropriate (a) to comply with applicable laws; (b) to comply with lawful requests and legal
    process, including to respond to requests from public and government authorities; (c) to enforce our Policy; and (d)
    to protect our rights, privacy, safety or property, and/or that of you or others.</li>
</ul>
<p>We may share this information with service providers who perform services on our behalf, such as those services listed above,
  or other services like processing information requests, displaying stored data you access, to assist us in marketing, to
  conduct audits, etc. Those companies will be permitted to obtain only the personal information they need to provide the
  service they provide, will be required to maintain the confidentiality of the information, and will be prohibited from
  using it for any other purpose.</p>
<p>We may also use information you provide to better serve you, and, if you have given your consent for us to do so, to send
  you email or text messages concerning offers from our partners and other third parties that we think may be of interest
  to you. If you do not wish to receive marketing emails, you may adjust your “Personal Information Preferences” as described
  below or follow the “unsubscribe” or “stop” instructions included within each communication.</p>
<p>We will only retain your personally identifiable information as long as reasonably required to provide you with the Service
  unless a longer retention period is required or permitted by law (for example, for regulatory purposes). </p>
<p>You may contact us anytime to opt-out of: (i) direct marketing communications; (ii) our collection of sensitive personal
  data; (iii) any new processing of your personal data that we may carry out beyond the original purpose; or (iv) the transfer
  of your personal data outside the EEA. Please note that your use of some of the Service may be ineffective upon opt-out.
  You may also: (A) access the data we hold about you at any time via your account or by contacting us directly; (B) update
  or correct any inaccuracies in your personal data by contacting us; (C) move your data to other service provider; (D) in
  certain situations, for example when the data we hold about you is no longer relevant or is incorrect, you can request
  that we erase your data. You may contact us at support@statmuse.com anytime for any other questions you may have about
  your personally identifiable information and our use of it. </p>
<p>b.
  <em>Disclosure</em>: As a general rule, StatMuse will not disclose any of your personally identifiable information except under
  one of the following circumstances: we have your permission, including the permission granted by your acceptance of this
  Privacy Policy; we determine in good faith that it is legally required to be revealed by any relevant statute, regulation,
  ordinance, rule, administrative or court order, decree, or subpoena; it is information that we determine must be disclosed
  to correct what we believe to be false or misleading information or to address activities that we believe to be manipulative,
  deceptive or otherwise a violation of law; where you are otherwise notified at the time we collect the data; where we need
  to share your information to provide the product or service you have requested; when such disclosure is made subject to
  confidentiality restrictions in connection with a sale, merger, transfer, exchange, or other disposition (whether of assets,
  stock, or otherwise) of all or a portion of the business conducted by StatMuse. StatMuse may share the non-personally identifiable
  information that StatMuse gathers, in aggregate form only, with advertisers and other partners. </p>
<p>
  <strong>3. Children's Policy</strong>
</p>
<p>The Terms of Service clearly provide that Users must be (i) 18 or older, or (ii) 13 and older if either (a) an emancipated
  minor, or (b) he/she possess legal parental or guardian consent. StatMuse does not knowingly collect personally identifiable
  information from users under 13. In the event that we learn that we have collected any personal information from a user
  under the age of 13, we will attempt to identify and delete that information from our database. </p>
<p>
  <strong>4. International Usage</strong>
</p>
<p>The Service is owned by StatMuse and may be accessed in the United States and abroad. For data protection purposes, StatMuse
  is the controller and, unless otherwise noted, is also the processor of data. Information collected may be retained, and
  may be stored, processed, accessed, and used in jurisdictions whose privacy laws may be different and less protective than
  those of your home jurisdiction. If you are located outside of the United States, please note that the information you
  provide to us may be transferred to the United States. By using the Service, application and/or website, you consent to
  such transfer. We will take reasonable steps to ensure that your data is treated securely and in accordance with this Policy.
  </p>
<p>
  <strong>5. How Does A User Change Or Update Information?</strong>
</p>
<p>If you have any questions or concerns about this privacy policy or would like the personally identifiable information that
  you have provided to be removed from our files, please contact StatMuse via email at support@statmuse.com.</p>
<p>
  <strong>6. Security and Encryption</strong>
</p>
<p>We follow generally accepted industry standards to help protect your personal information. No method of transmission over
  the internet, mobile technology, or method of electronic storage, is completely secure. Therefore, while we endeavor to
  maintain physical, electronic, and procedural safeguards to protect the confidentiality of the information that we collect
  online, we cannot guarantee its absolute security. Our Service has security measures in place designed to protect against
  the loss, misuse and alteration of the information under our control. We use standard Secure Socket Layer (SSL) encryption
  that encodes information for such transmissions. All Service information is maintained on secure servers. Access to stored
  data is protected by multi-layered security controls including firewalls, role-based access controls and passwords. You
  are responsible to keep your password secure. If you have reason to believe that your interaction with us is no longer
  secure (for example, if you feel that the security of any account you might have with us has been compromised), please
  immediately notify us of the problem by contacting us at support@statmuse.com.</p>
<p>
  <strong>7. Changes to This Policy</strong>
</p>
<p>We reserve the right to change the terms of this privacy policy at any time. When we make changes, we will revise the “last
  updated” date at the top of the policy. If there are material changes to this statement or in how we will use your personal
  information, we will notify you by prominently posting a notice of such changes here or on our home page, or by sending
  you an email. We encourage you to review this policy whenever you visit one of our websites or applications.</p>
<p>© 2018 StatMuse, Inc. All rights reserved.</p>
</div>
</template>

<script>
export default {};
</script>

<style scoped>
.policy {
  text-align: left;
}
</style>



// WEBPACK FOOTER //
// src/components/PrivacyPolicy.vue